<script setup lang="ts">
import type { Form } from '#ui/types';
import { z } from 'zod';

import { type BackendErrorEntity, type ErrorType, useApiRequestPasswordReset } from '~/apiClient';
import { NotificationDuration } from '~/types/notifications.type';

// PROPS
const { email } = defineProps({
    email: { type: String, default: undefined },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const { notifySuccess } = useNotification();
const { closeRequestPasswordResetModal } = useModals();
const { getErrorDescription } = useBackendErrors();
const { isMobile } = useResponsive();
const { mutateAsync: mutateSubmit, isPending: isSubmitting } = useApiRequestPasswordReset();

// DATA
const formSchema = z.object({
    email: zodRequiredString(tt('validation.msg.required', { field: tt('login.email') })).email(
        tt('validation.msg.invalid', { field: tt('login.email') })
    ),
});
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;

const formData: FormData = reactive({
    email: email ?? '',
});
const form = useTemplateRef<Form<FormSchema>>('form');
const submitErrorMessage = ref<string | null>(null);

// METHODS
const onSubmit = async (): Promise<void> => {
    submitErrorMessage.value = null;
    mutateSubmit({ data: { email: formData.email } })
        .then(() => {
            closeRequestPasswordResetModal();
            notifySuccess({
                title: tt('passwordReset.request.onSuccessTitle'),
                description: tt('passwordReset.request.onSuccessDescription'),
                duration: NotificationDuration.Long,
            });
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            if (isBackendError(apiError.data)) {
                submitErrorMessage.value = getErrorDescription(apiError.data.error);
            } else {
                submitErrorMessage.value = tt('notifications.genericError');
            }
        });
};
</script>

<template>
    <UModal :fullscreen="isMobile" :transition="!isMobile" :title="tt('passwordReset.request.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <UForm ref="form" class="flex flex-col items-center gap-y-4 text-center" :state="formData" :schema="formSchema" @submit="onSubmit">
                <p class="text-muted text-center">{{ tt('passwordReset.request.subTitle') }}</p>
                <UFormField class="w-full" :label="tt('common.email')" name="email" required>
                    <UInput v-model.trim="formData.email" autofocus autocomplete="email" :disabled="isSubmitting" />
                </UFormField>

                <BaseAlert v-if="submitErrorMessage" :title="submitErrorMessage" type="error" />
            </UForm>
        </template>
        <template #footer>
            <BaseIconBgButton block :label="tt('passwordReset.request.submitButton')" size="xl" :loading="isSubmitting" @click="form?.submit()" />
        </template>
    </UModal>
</template>
